import React, { useMemo } from "react";
import { Chip, Avatar } from "@mui/material";
import classNames from "classnames";
import AnniversaryAutomatorDialog from "../MailPro/components/Notes/AnniversaryAutomator/AnniversaryAutomatorDialog";
import { MailProConstants } from "../MailPro/PmaMailingList/MailProConstants";
import { renderLogicUtil } from "../RenderLogicUtil";

const NavbarFilterChips = React.memo(
  ({
    countHOMap,
    hoQuickFilter,
    shouldAnniversaryCountButtonFlash,
    propertyData,
    hoFilter,
    mlsAreas,
    currentArea,
    agentData,
    isMailProLocked,
    isLoading,
    classes,
    updateHOQuickFilter,
    handleNewMailingChipButtons,
  }) => {
    const newMailingHOPropertiesCount = useMemo(
      () => isLoading ? 0 : renderLogicUtil.countMailProInitProperties(propertyData, true, MailProConstants.newMailing, hoFilter),
      [propertyData, hoFilter, isLoading]
    );

    const newlyConstructedHOPropertiesCount = useMemo(
      () => isLoading ? 0 : renderLogicUtil.countMailProInitProperties(propertyData, true, MailProConstants.isNewProperty, hoFilter),
      [propertyData, hoFilter, isLoading]
    );

    const selectedStyle = (key) => ({
      width: "fit-content",
      borderColor: hoQuickFilter === key ? "#3D85C6" : undefined,
      color: hoQuickFilter === key ? "#3D85C6" : undefined,
    });

    const selectedClasses = (key) =>
      classNames(classes.boldFontWeight, {
        [classes.chipSelected]: hoQuickFilter === key,
      });

    const sharedChipClasses = {
      avatarSmall: classes.avatarChip,
      clickable: classes.clickableChip,
      deleteIconColorPrimary: classes.deleteIconColorPrimary,
    };

    return (
      <div
        style={{
          display: "grid",
          gridTemplateRows: "auto auto",
          gridAutoFlow: "column",
          overflow: "visible",
          gridGap: "4px 16px",
          alignItems: "center",
        }}
      >
        <AnniversaryAutomatorDialog
          agentData={agentData}
          defaultOpen={false}
          mlsAreas={mlsAreas}
          currentArea={currentArea}
          cameFrom="Anniversary"
          key="anniversary_automator_from_navbar"
          isMailProLocked={isMailProLocked}
        >
          <Chip
            size="small"
            avatar={<Avatar>{isLoading ? "–" : countHOMap.anniversary}</Avatar>}
            label="HAPPY ANNIVERSARY!"
            clickable
            onDelete={
              hoQuickFilter === "anniversary"
                ? () => updateHOQuickFilter("")
                : null
            }
            color="default"
            variant="outlined"
            onClick={() => updateHOQuickFilter("anniversary")}
            style={selectedStyle("anniversary")}
            classes={{
              root: selectedClasses("anniversary"),
              ...sharedChipClasses,
            }}
            disabled={isLoading || countHOMap.totalAnniversaries === 0}
            className={
              !isLoading && countHOMap.anniversary !== 0 && shouldAnniversaryCountButtonFlash
                ? classes.flashButton
                : null
            }
          />
        </AnniversaryAutomatorDialog>

        <Chip
          size="small"
          avatar={<Avatar>{isLoading ? "–" : countHOMap.absentee}</Avatar>}
          label="ABSENTEE HOMEOWNERS"
          clickable
          onDelete={
            hoQuickFilter === "absentee"
              ? () => updateHOQuickFilter("")
              : null
          }
          color="default"
          variant="outlined"
          onClick={() => updateHOQuickFilter("absentee")}
          style={selectedStyle("absentee")}
          classes={{
            root: selectedClasses("absentee"),
            ...sharedChipClasses,
          }}
          disabled={isLoading || countHOMap.absentee === 0}
        />

        {newMailingHOPropertiesCount > 0 && (
          <Chip
            size="small"
            avatar={<Avatar>{newMailingHOPropertiesCount || 0}</Avatar>}
            label={`NEW HOMEOWNER${newMailingHOPropertiesCount > 1 ? "S" : ""}`}
            clickable
            onDelete={
              hoQuickFilter === MailProConstants.newMailing
                ? () => updateHOQuickFilter("")
                : null
            }
            color="default"
            variant="outlined"
            onClick={() => handleNewMailingChipButtons(MailProConstants.newMailing)}
            style={selectedStyle(MailProConstants.newMailing)}
            classes={{
              root: selectedClasses(MailProConstants.newMailing),
              ...sharedChipClasses,
            }}
            className={classes.flashButton}
            disabled={!newMailingHOPropertiesCount}
          />
        )}

        {newlyConstructedHOPropertiesCount > 0 && (
          <Chip
            size="small"
            avatar={<Avatar>{newlyConstructedHOPropertiesCount || 0}</Avatar>}
            label={`NEW ${
              newlyConstructedHOPropertiesCount > 1 ? "PROPERTIES" : "PROPERTY"
            } TO ADD`}
            clickable
            onDelete={
              hoQuickFilter === MailProConstants.isNewProperty
                ? () => updateHOQuickFilter("")
                : null
            }
            color="default"
            variant="outlined"
            onClick={() => handleNewMailingChipButtons(MailProConstants.isNewProperty)}
            style={selectedStyle(MailProConstants.isNewProperty)}
            classes={{
              root: selectedClasses(MailProConstants.isNewProperty),
              ...sharedChipClasses,
            }}
            className={classes.flashButton}
            disabled={!newlyConstructedHOPropertiesCount}
          />
        )}
      </div>
    );
  }
);

NavbarFilterChips.displayName = "NavbarFilterChips";

export default NavbarFilterChips;
